import type { JSX } from "react";
import { useMemo } from "react";

import {
  skipToken,
  useListNotificationsQuery,
  useListSubscriptionsQuery,
} from "metabase/api";
import type { NotificationListItem } from "metabase/notifications/types";
import { useSelector } from "metabase/redux";
import { Outlet, useNavigate } from "metabase/router";
import {
  canManageSubscriptions as canManageSubscriptionsSelector,
  getUser,
} from "metabase/selectors/user";
import { parseTimestamp } from "metabase/utils/time-dayjs";

import { getArchiveUrl, getHelpUrl, getUnsubscribeUrl } from "../../actions";
import { NotificationList } from "../../components/NotificationList";

export const NotificationsApp = (): JSX.Element | null => {
  const user = useSelector(getUser);
  const canManageSubscriptions = useSelector(canManageSubscriptionsSelector);

  const navigate = useNavigate();

  const { data: pulses = [] } = useListSubscriptionsQuery({
    creator_or_recipient: true,
  });

  const { data: questionNotifications = [] } = useListNotificationsQuery(
    user
      ? {
          creator_or_recipient_id: user.id,
          include_inactive: false,
        }
      : skipToken,
  );

  const items = useMemo(() => {
    const combinedItems: NotificationListItem[] = [
      ...questionNotifications.map((alert) => ({
        item: alert,
        type: "question-notification" as const,
      })),
      ...pulses.map((pulse) => ({
        item: pulse,
        type: "pulse" as const,
      })),
    ];

    return combinedItems.sort(
      (a, b) =>
        parseTimestamp(b.item.created_at).unix() -
        parseTimestamp(a.item.created_at).unix(),
    );
  }, [pulses, questionNotifications]);

  const onHelp = () => navigate(getHelpUrl());
  const onUnsubscribe = ({ item, type }: NotificationListItem) =>
    navigate(getUnsubscribeUrl(item, type));
  const onArchive = ({ item, type }: NotificationListItem) =>
    navigate(getArchiveUrl(item, type));

  if (!user) {
    return null;
  }

  return (
    <NotificationList
      listItems={items}
      user={user}
      canManageSubscriptions={canManageSubscriptions}
      onHelp={onHelp}
      onUnsubscribe={onUnsubscribe}
      onArchive={onArchive}
    >
      <Outlet />
    </NotificationList>
  );
};
